import { apiClient } from '@/lib/api-client'

export interface ConceptBuildFrame {
  stage: string
  percentage: number
  index?: number
  total?: number
  concepts?: number
  grouped?: number
  current?: string
  message?: string
}

export interface ConceptBuildEvent {
  seq: number
  new_concept: { id: number; members: { id: number; lemma: string; lang: string }[] }
}

export interface ConceptBuildStatus {
  /** Seconds since the build started, from the server. */
  elapsed?: number
  status: 'idle' | 'running' | 'done' | 'error'
  frame?: ConceptBuildFrame
  seq: number
  events: ConceptBuildEvent[]
  result?: { concepts: number; grouped: number } | null
  message?: string | null
}

/**
 * The build is a detached server-side job: starting returns immediately and
 * the run outlives this tab. Progress comes from polling the status endpoint,
 * which also replays per-concept events (ring-buffered) so a client that
 * navigated away and back catches up on what it missed.
 */
export async function startConceptBuild(collectionId: number): Promise<void> {
  await apiClient.post(`/collections/${collectionId}/concepts/build`)
}

export async function getConceptBuildStatus(
  collectionId: number,
  since: number,
): Promise<ConceptBuildStatus> {
  const { data } = await apiClient.get<ConceptBuildStatus>(
    `/collections/${collectionId}/concepts/build/status`,
    { params: { since } },
  )
  return data
}

/** Stop a running build at the next safe point; work already done is kept. */
export async function cancelConceptBuild(collectionId: number): Promise<void> {
  await apiClient.delete(`/collections/${collectionId}/concepts/build`)
}
